import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import type { Organization, RoleInfo } from "@/types";

interface AddUserDialogProps {
  assignable: RoleInfo[];
  onClose: () => void;
  onCreated: () => void;
  isSuperAdmin: boolean;
}

export function AddUserDialog({ assignable, onClose, onCreated, isSuperAdmin }: AddUserDialogProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(assignable[0]?.name ?? "");
  const [orgId, setOrgId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: orgsData } = useQuery({
    queryKey: queryKeys.organizations,
    queryFn: () => api.listOrganizations(),
    enabled: isSuperAdmin,
  });
  const orgs: Organization[] = orgsData?.organizations ?? [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (isSuperAdmin) {
        await api.createUserInOrg({ username, password, role, org_id: orgId });
      } else {
        await api.createUser({ username, password, role });
      }
      onCreated();
      onClose();
    } catch (err) {
      setError((err as Error).message || "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="w-full max-w-sm rounded-card bg-white p-6 shadow-card" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-4 text-lg font-semibold text-near-black">Add user</h3>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {isSuperAdmin && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Organization</label>
              <select value={orgId ?? ""} onChange={(e) => setOrgId(e.target.value ? Number(e.target.value) : null)} className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus">
                <option value="">None</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Username</label>
            <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus" autoFocus required />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus" required minLength={4} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus">
              {assignable.map((r) => <option key={r.name} value={r.name}>{r.label}</option>)}
            </select>
          </div>
          {error && <p className="rounded-badge bg-rose-100 px-3 py-2 text-xs font-medium text-error-text">{error}</p>}
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-btn px-4 py-2 text-sm font-medium text-text-secondary transition-colors hover:text-near-black">Cancel</button>
            <button type="submit" disabled={saving} className="rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch disabled:opacity-50">{saving ? "Creating..." : "Create user"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
